import { useEffect, useState } from 'react';
import { SUGGESTION_DEBOUNCE_MS } from '@visious/core';
import { fetchSuggestions } from '../api/search';
import { getPlatform } from '../platform/instance';

/**
 * The suggestions list for what is currently typed, debounced.
 *
 * **Not TanStack Query, and this is the third deliberate exception rather than an oversight.** Query's
 * job is to cache and share a *resource*; this is a keystroke-shaped stream where the only interesting
 * behaviours are "cancel the previous one" and "throw away an answer that arrived late", and the query
 * key would change on every character — a cache entry per prefix, none of which is ever read twice.
 * `useComments.ts` makes the same argument at more length.
 *
 * Two failure modes it has to handle, and both are ordinary rather than exceptional:
 *
 * - **A late answer.** Type "lin", then "linux": if the first reply lands second, the list under the
 *   box describes a query nobody has on screen. The `live` flag drops it. (`suggestionsMatch` in core
 *   is the same check made from the other side, for a caller that keeps the payload.)
 * - **Any failure at all.** A dropdown that does not appear costs nothing; an error message under a
 *   search box while someone is mid-word is unusable. So a failed fetch resolves to an empty list and
 *   says nothing, which is the one place in this app where swallowing an error is the right call.
 */
export function useSuggestions(typed: string): readonly string[] {
  const [items, setItems] = useState<readonly string[]>([]);
  const q = typed.trim();

  useEffect(() => {
    if (q === '') return;

    let live = true;
    const timer = globalThis.setTimeout(() => {
      void (async () => {
        const boot = await getPlatform();
        if (!live || !boot.ok) return;
        const result = await fetchSuggestions(boot.platform, q);
        if (!live) return;
        setItems(result.ok ? result.value.suggestions : []);
      })();
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      live = false;
      globalThis.clearTimeout(timer);
    };
  }, [q]);

  return blankOrItems(q, items);
}

/**
 * An empty box has no suggestions, **derived** rather than cleared in the effect.
 *
 * Clearing was the obvious version and `react-hooks/set-state-in-effect` refused it, correctly: a
 * setState in an effect body is a second render for a fact that was already knowable during the first.
 * Deriving also fixes a frame nobody would have noticed until they saw it — clearing the box would have
 * left the previous list on screen for exactly one render.
 *
 * The non-empty case is genuinely asynchronous and stays in state, and the old list stays visible until
 * the new one lands, which is what makes the box feel stable while someone is typing.
 */
function blankOrItems(q: string, items: readonly string[]): readonly string[] {
  return q === '' ? EMPTY : items;
}

const EMPTY: readonly string[] = [];
